import React, { createContext, useContext, useState, useEffect } from 'react';

const themes = {
  dark: {
    label: 'Dark',
    '--dark-bg': '#262626',
    '--deep-dark': '#1E1E1E',
    '--gray-1': '#BCB4B4',
    '--gray-2': '#363636',
    '--highlight-blue': '#007bff',
  },
  light: {
    label: 'Light',
    '--dark-bg': '#f0f0f0',
    '--deep-dark': '#dcdcdc',
    '--gray-1': '#2c2c2c',
    '--gray-2': '#c8c8c8',
    '--highlight-blue': '#0056b3',
  },
  monochrome: {
    label: 'Monochrome',
    '--dark-bg': '#ffffff',
    '--deep-dark': '#e8e8e8',
    '--gray-1': '#111111',
    '--gray-2': '#cccccc',
    '--highlight-blue': '#444444',
  },
  // Popular dev editor theme — cool arctic blue-grays
  nord: {
    label: 'Nord',
    '--dark-bg': '#2E3440',
    '--deep-dark': '#242933',
    '--gray-1': '#D8DEE9',
    '--gray-2': '#3B4252',
    '--highlight-blue': '#88C0D0',
  },
  // Classic dark purple — one of the most downloaded VS Code themes
  dracula: {
    label: 'Dracula',
    '--dark-bg': '#282A36',
    '--deep-dark': '#21222C',
    '--gray-1': '#F8F8F2',
    '--gray-2': '#44475A',
    '--highlight-blue': '#BD93F9',
  },
  // Rich greens — popular in fintech and sustainability brands
  forest: {
    label: 'Forest',
    '--dark-bg': '#1A2E1A',
    '--deep-dark': '#0F1F0F',
    '--gray-1': '#A8D5A2',
    '--gray-2': '#243324',
    '--highlight-blue': '#4CAF50',
  },
  // Tailwind-inspired cool slate — widely used in modern dev portfolios
  slate: {
    label: 'Slate',
    '--dark-bg': '#0F172A',
    '--deep-dark': '#020617',
    '--gray-1': '#CBD5E1',
    '--gray-2': '#1E293B',
    '--highlight-blue': '#38BDF8',
  },
  // Luxury black & gold — warm cream text, metallic gold accents
  gold: {
    label: 'Black & Gold',
    '--dark-bg': '#111000',
    '--deep-dark': '#0A0800',
    '--gray-1': '#E8D9B4',
    '--gray-2': '#1E1A00',
    '--highlight-blue': '#C9A227',
    '--highlight-gradient': 'linear-gradient(135deg, #7A5C0A 0%, #C9A227 35%, #FBF0A8 52%, #C9A227 68%, #7A5C0A 100%)',
  },
  // Deep crimson red — dark warm blacks with bold red accents
  crimson: {
    label: 'Crimson',
    '--dark-bg': '#150808',
    '--deep-dark': '#0D0404',
    '--gray-1': '#F0C8C8',
    '--gray-2': '#200D0D',
    '--highlight-blue': '#E63946',
  },
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('site-theme');
    if (saved && themes[saved]) return saved;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  });

  useEffect(() => {
    // Clear every CSS variable defined across all themes first,
    // so theme-specific vars (e.g. --highlight-gradient) don't bleed
    // into themes that don't define them.
    const allKeys = new Set(
      Object.values(themes).flatMap((t) =>
        Object.keys(t).filter((k) => k.startsWith('--'))
      )
    );
    allKeys.forEach((key) => document.documentElement.style.removeProperty(key));

    // Apply the selected theme's variables
    Object.entries(themes[theme]).forEach(([key, val]) => {
      if (key.startsWith('--')) {
        document.documentElement.style.setProperty(key, val);
      }
    });
    localStorage.setItem('site-theme', theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
